import { useMemo } from "react";
import { useSelector } from "react-redux";
import { TextStyle, TextMetrics } from "pixi.js";
import { useDimensions } from "../Contexts/DimensionsContext";
import { USER_INTERFACE_BUTTON_CONFIG } from "../textureConfigs";
import { PixiSettings } from "../pixiSettings";
import { FONT_CONFIG } from "../textureConfigs";
import { formatToCustomLocale } from "../utilities";
import { selectCurrency, selectBets } from "../State/selectors";

/**
 * Hook for calculating PopiToolbar dimensions with width capping
 * - Mobile landscape: capped dynamically at uiSymbolSize * MAX_TOOLBAR_WIDTH_MOBILE_LANDSCAPE_MULTIPLIER
 * - All other cases: capped at MAX_TOOLBAR_WIDTH (1920px)
 * Simpler alternative to useToolbarDimensions for the bottom toolbar
 * Returns toolbar object with width, height, and layout positions based on device/orientation
 */
export const usePopiToolbarDimensions = () => {
  const { dimensions, device, orientation, getResponsiveConfig, uiSymbolSize, uiSymbolScale } =
    useDimensions();
  const currency = useSelector(selectCurrency);
  const bets = useSelector(selectBets);

  return useMemo(() => {
    // Determine max width based on device and orientation
    // For mobile landscape, calculate dynamically based on UI symbol width
    const maxWidth =
      device === PixiSettings.DEVICE.MOBILE &&
      orientation === PixiSettings.ORIENTATION.LANDSCAPE
        ? uiSymbolSize *
          PixiSettings.MAX_TOOLBAR_WIDTH_MOBILE_LANDSCAPE_MULTIPLIER
        : PixiSettings.MAX_TOOLBAR_WIDTH;

    // Cap width at the determined max width
    const width = Math.min(dimensions.width, maxWidth);

    // Get button config to calculate toolbar height
    const buttonConfig = getResponsiveConfig(
      USER_INTERFACE_BUTTON_CONFIG,
      true
    );

    // Calculate toolbar height based on device and orientation
    const height =
      device === PixiSettings.DEVICE.MOBILE
        ? orientation === PixiSettings.ORIENTATION.PORTRAIT
          ? 0
          : buttonConfig.height * 1.2
        : device === PixiSettings.DEVICE.TABLET
        ? orientation === PixiSettings.ORIENTATION.PORTRAIT
          ? 0
          : buttonConfig.height * 1.85
        : buttonConfig.height * 1.85;

    // Calculate MaxBet button width for layout positioning
    const calculateMaxBetWidth = () => {
      // Max bet button configuration - matches ButtonMaxBet component
      const MAX_BET_BUTTON_CONFIG = {
        BASE_WIDTH: 80,
        BASE_HEIGHT: 80,
        DEVICE_CONFIGS: {
          [PixiSettings.DEVICE.MOBILE]: {
            [PixiSettings.ORIENTATION.PORTRAIT]: {
              deviceMultiplier: 1.97,
            },
            [PixiSettings.ORIENTATION.LANDSCAPE]: {
              deviceMultiplier: 1.75,
            },
          },
          [PixiSettings.DEVICE.TABLET]: {
            [PixiSettings.ORIENTATION.PORTRAIT]: {
              deviceMultiplier: 2.3,
            },
            [PixiSettings.ORIENTATION.LANDSCAPE]: {
              deviceMultiplier: 1.1,
            },
          },
          [PixiSettings.DEVICE.DESKTOP]: {
            deviceMultiplier: 1.0,
          },
        },
      };

      const MAX_BET_BUTTON_CONFIG_DESKTOP = {
        BASE_WIDTH: 93,
        BASE_HEIGHT: 64,
        DEVICE_CONFIGS: {
          [PixiSettings.DEVICE.DESKTOP]: {
            deviceMultiplier: 0.9,
          },
        },
      };

      const maxBetConfig =
        device === PixiSettings.DEVICE.DESKTOP
          ? getResponsiveConfig(MAX_BET_BUTTON_CONFIG_DESKTOP, true)
          : getResponsiveConfig(MAX_BET_BUTTON_CONFIG, true);

      return maxBetConfig.width;
    };

    // Calculate component widths for layout positioning
    const calculateBalanceWidth = () => {
      // Get font multiplier for balance based on device/orientation
      let fontMultiplier = 1.0;
      if (device === PixiSettings.DEVICE.MOBILE) {
        fontMultiplier = orientation === PixiSettings.ORIENTATION.PORTRAIT ? 1.0 : 1.1;
      } else if (device === PixiSettings.DEVICE.TABLET) {
        fontMultiplier = orientation === PixiSettings.ORIENTATION.PORTRAIT ? 1.65 : 1.25;
      } else {
        fontMultiplier = 1.0; // Desktop
      }

      const baseFontSize = 32;
      const responsiveFontSize = baseFontSize * fontMultiplier;
      const idealFontSize = Math.round(responsiveFontSize * uiSymbolScale);

      // Calculate max balance width (7-digit balance)
      const maxBalance = 9999999;
      const maxBalanceText = `${formatToCustomLocale(maxBalance)} ${currency}`;
      const tempStyle = new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: idealFontSize,
        fontWeight: "700",
      });
      const maxBalanceMetrics = TextMetrics.measureText(maxBalanceText, tempStyle);

      // Return the max width (this is what BalanceDisplay uses internally)
      return maxBalanceMetrics.width;
    };

    const calculateBetWidth = () => {
      // Get font multiplier for bet based on device/orientation
      let fontMultiplier = 1.0;
      if (device === PixiSettings.DEVICE.MOBILE) {
        fontMultiplier = orientation === PixiSettings.ORIENTATION.PORTRAIT ? 1.0 : 1.1;
      } else if (device === PixiSettings.DEVICE.TABLET) {
        fontMultiplier = orientation === PixiSettings.ORIENTATION.PORTRAIT ? 1.65 : 1.25;
      } else {
        fontMultiplier = 1.0; // Desktop
      }

      const baseFontSize = 32;
      const responsiveFontSize = baseFontSize * fontMultiplier;
      const fontSize = Math.round(responsiveFontSize * uiSymbolScale);

      // Calculate max bet width based on largest bet in bets array
      if (!bets || bets.length === 0) {
        return 100; // Fallback width
      }

      const maxBet = Math.max(...bets);
      const maxBetText = `${formatToCustomLocale(maxBet)} ${currency}`;
      const tempStyle = new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: fontSize,
        fontWeight: "700",
      });
      const maxBetMetrics = TextMetrics.measureText(maxBetText, tempStyle);

      return maxBetMetrics.width;
    };

    // Calculate component widths
    const balanceWidth = calculateBalanceWidth();
    const betWidth = calculateBetWidth();
    const maxBetWidth = calculateMaxBetWidth();

    // Calculate positions based on device/orientation
    const calculatePositions = () => {
      // Device and orientation specific gap configurations (only for sequential layouts)
      let gap; // Gap between Balance and Bet (or Balance and MaxBet on tablet landscape)
      let maxBetGap; // Gap between Bet and MaxBet

      if (device === PixiSettings.DEVICE.MOBILE && orientation === PixiSettings.ORIENTATION.LANDSCAPE) {
        gap = buttonConfig.width * 2.0; // Mobile Landscape gap
        maxBetGap = buttonConfig.width * 2.0; // Mobile Landscape MaxBet gap
      } else if (device === PixiSettings.DEVICE.TABLET && orientation === PixiSettings.ORIENTATION.LANDSCAPE) {
        gap = buttonConfig.width *1.1 ; // Tablet Landscape gap (Balance to MaxBet)
        maxBetGap = buttonConfig.width * 2.7; // Tablet Landscape MaxBet gap (MaxBet to Bet)
      } else if (device === PixiSettings.DEVICE.DESKTOP) {
        gap = buttonConfig.width * 3.6; // Desktop gap (Balance to Bet)
        maxBetGap = buttonConfig.width * 1.75; // Desktop MaxBet gap (Bet to MaxBet) - smaller gap
      }

      if (device === PixiSettings.DEVICE.MOBILE) {
        if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
          // Portrait: Balance on left, Bet on right (keep separate positioning)
          // MaxBet uses original positioning logic from ButtonMaxBet component
          return {
            balance: {
              x: buttonConfig.width * 0.5,
              width: balanceWidth,
            },
            bet: {
              x: width - buttonConfig.width * 1.5,
              width: betWidth,
            },
          };
        } else {
          // Landscape: Balance -> Bet (left to right)
          // MaxBet uses original positioning logic from ButtonMaxBet component
          const balanceX = buttonConfig.width * 1.5;
          const betX = balanceX + (balanceWidth / 2) + gap + (betWidth / 2);
          return {
            balance: {
              x: balanceX,
              width: balanceWidth,
            },
            bet: {
              x: betX,
              width: betWidth,
            },
          };
        }
      } else if (device === PixiSettings.DEVICE.TABLET) {
        if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
          // Portrait: Balance on left, Bet on right (keep separate positioning)
          // MaxBet uses original positioning logic from ButtonMaxBet component
          return {
            balance: {
              x: buttonConfig.width * 0.75,
              width: balanceWidth,
            },
            bet: {
              x: width - buttonConfig.width * 2,
              width: betWidth,
            },
          };
        } else {
          // Landscape: Balance -> MaxBet -> Bet (left to right)
          // MaxBet goes between Balance and Bet on tablet landscape
          // Account for center-anchored components
          const balanceX = buttonConfig.width * 0.5;
          const maxBetX = balanceX + (balanceWidth / 2) + gap + (maxBetWidth / 2);
          const betX = maxBetX + (maxBetWidth / 2) + gap + (betWidth / 2);
          return {
            balance: {
              x: balanceX,
              width: balanceWidth,
            },
            bet: {
              x: betX,
              width: betWidth,
            },
            maxBet: {
              x: maxBetX,
              width: maxBetWidth,
            },
          };
        }
      } else {
        // Desktop: Balance -> Bet -> MaxBet (left to right)
        // Account for center-anchored components
        // Use different gaps: 'gap' between Balance and Bet, 'maxBetGap' between Bet and MaxBet
        const balanceX = buttonConfig.width * 1.75;
        const betX = balanceX + (balanceWidth / 2) + gap + (betWidth / 2);
        const maxBetX = betX + (betWidth / 2) + maxBetGap + (maxBetWidth / 2);
        return {
          balance: {
            x: balanceX,
            width: balanceWidth,
          },
          bet: {
            x: betX,
            width: betWidth,
          },
          maxBet: {
            x: maxBetX,
            width: maxBetWidth,
          },
        };
      }
    };

    const layout = calculatePositions();

    return { width, height, layout };
  }, [
    dimensions.width,
    device,
    orientation,
    getResponsiveConfig,
    uiSymbolSize,
    uiSymbolScale,
    currency,
    bets,
  ]);
};
